import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    IndianRupee, Clock, Banknote, Wallet, RefreshCw, ChevronDown, ChevronUp, CalendarRange, Ruler, Calculator,
    Calendar as CalIcon, CreditCard, Search, X, Download, AlertCircle
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Modal from '../../ui/Modal';

const API_BASE = 'https://mukadampro.shirpur.store';

// --- HELPER FUNCTIONS ---
const getToday = () => new Date().toISOString().split("T")[0];  /* 🌟 NAYA CODE YAHAN JODEN 🌟 */

const formatCurrency = (amount) => {
  return "Rs. " + (Number(amount) || 0).toLocaleString('en-IN');
};

const formatDate = (dateStr) => {
    if(!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric'
    });
};

const formatTimeForDisplay = (t) => { 
    if (!t) return ""; 
    let timeStr = String(t).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr; 
    let [h, m] = timeStr.split(':'); 
    h = parseInt(h, 10) || 0; 
    const ampm = h >= 12 ? 'PM' : 'AM'; 
    return `${h % 12 || 12}:${m || '00'} ${ampm}`; 
};

const formatDateTime = (dateStr) => {
    if(!dateStr) return "-";
    const d = new Date(dateStr);
    const hasTime = dateStr.includes(' ') || dateStr.includes(':');
    if (hasTime) {
        return d.toLocaleString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    }
    return formatDate(dateStr);
};

// --- VOICE FORMATTERS (Copied exactly from Owner side) ---
const formatVoiceCurrency = (amt) => {
    const num = Number(amt) || 0;
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    if (paise > 0) return `${rupees} रुपये ${paise} पैसे`;
    return `${rupees} रुपये`; 
};

const formatVoiceNumber = (num) => {
    const n = Number(num) || 0;
    if (Number.isInteger(n) || n % 1 === 0) return Math.floor(n).toString(); 
    const parts = n.toString().split('.');
    return `${parts[0]} पॉइंट ${parts[1]}`;
};

export default function WorkerPaymentView({ user, appBg, handleVoiceClick: globalHandleVoiceClick, voiceAssist }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statFilterType, setStatFilterType] = useState('all');
    const [expandedTxn, setExpandedTxn] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewData, setViewData] = useState(null);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportDateStart, setReportDateStart] = useState('');
    const [reportDateEnd, setReportDateEnd] = useState('');

    // 🌟 2. UPDATED FILTER HISTORY (Date Search ke sath) 🌟
    const filteredHistory = useMemo(() => {
        let data = history;
        if (statFilterType === 'salary') data = data.filter(p => p.type === 'salary');
        if (statFilterType === 'advance') data = data.filter(p => p.type === 'advance');
        if (statFilterType === 'recovered') data = data.filter(p => p.type === 'salary' && parseFloat(p.advance_adjusted) > 0);
        
        // Date filter logic
        if (startDate) data = data.filter(p => p.date.split(' ')[0] >= startDate);
        if (endDate) data = data.filter(p => p.date.split(' ')[0] <= endDate);

        return data;
    }, [history, statFilterType, startDate, endDate]);

   // 🌟 3. PREMIUM LANDSCAPE PDF DOWNLOAD FUNCTION (For Worker) 🌟
    const handleDownloadPdf = async (e) => {
        if (e) e.preventDefault();

        let data = history;
        if(reportDateStart) data = data.filter(p => p.date.split(' ')[0] >= reportDateStart);
        if(reportDateEnd) data = data.filter(p => p.date.split(' ')[0] <= reportDateEnd);

        // 👇 NAYA LOGIC: Owner ki details direct database se fetch kar rahe hain
        let pData = {};
        try {
            const profileRes = await fetch(`${API_BASE}/get_owner_profile.php`);
            const profileJson = await profileRes.json();
            if (profileJson.status === 'success') {
                pData = profileJson.data;
            }
        } catch (err) {
            console.error("Failed to fetch owner profile", err);
        }

        const TEXTILE_INFO = {
            name: pData.textile_name ? pData.textile_name.toUpperCase() : "MUKADAMPRO TEXTILES",
            owner: pData.owner_name ? pData.owner_name.toUpperCase() : "OWNER NAME",
            mobile: pData.mobile || "-",
            email: pData.email || "-",
            gst: pData.gstin || pData.gst_number || "-",
            address: pData.address || "-",
            totalLooms: pData.total_looms || pData.total_factory_looms || "-"
        };

        // Landscape format for matching layout
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const centerX = 297 / 2;

        const drawWatermark = () => {
            doc.setTextColor(240, 245, 250); 
            doc.setFontSize(45);
            doc.setFont("helvetica", "bold");
            doc.text(TEXTILE_INFO.name, centerX, 110, { align: "center", angle: 35 });
        };

        drawWatermark();

        const originalAddPage = doc.addPage.bind(doc);
        doc.addPage = function(...args) {
            originalAddPage(...args);
            drawWatermark(); 
            return this;     
        };

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(15, 23, 42); 
        // 🌟 NAYA TITLE: SALARY / ADVANCE PAYMENT REPORT 🌟
        doc.text("SALARY / ADVANCE PAYMENT REPORT", centerX, 15, { align: "center" });

        const downloadTime = new Date().toLocaleString('en-IN', { 
            day: 'numeric', month: 'short', year: 'numeric', 
            hour: '2-digit', minute: '2-digit', hour12: true 
        });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Downloaded: ${downloadTime}`, 297 - 14, 15, { align: "right" });

        // 🌟 OWNER DETAILS SECTION 🌟
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 65, 85);
        doc.text(TEXTILE_INFO.name, centerX, 24, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);

        const line1 = `Owner: ${TEXTILE_INFO.owner}   |   Total Looms: ${TEXTILE_INFO.totalLooms}   |   Mobile: ${TEXTILE_INFO.mobile}`;
        doc.text(line1, centerX, 30, { align: "center" });

        const line2 = `Email: ${TEXTILE_INFO.email}   |   GST No: ${TEXTILE_INFO.gst}`;
        doc.text(line2, centerX, 35, { align: "center" });

        const line3 = `Address: ${TEXTILE_INFO.address}`;
        doc.text(line3, centerX, 40, { align: "center" });

        doc.setDrawColor(226, 232, 240); 
        doc.setLineWidth(0.5);
        doc.line(14, 44, 297 - 14, 44);

        // 🌟 STRIP LOGIC (Name, Shift aur Date) 🌟
        const workerName = user?.name ? user.name.toUpperCase() : "WORKER";
        const shift = user?.shift ? user.shift.toUpperCase() : "-";

        const fromDateStr = reportDateStart ? new Date(reportDateStart).toLocaleDateString("en-GB", { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
        const toDateStr = reportDateEnd ? new Date(reportDateEnd).toLocaleDateString("en-GB", { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
        const formattedDateRange = `${fromDateStr} to ${toDateStr}`;

        let currentY = 52; // Adjusted position after Owner details

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        
        // Light Blue/Grey Box Background (Strip)
        doc.setFillColor(241, 245, 249);
        doc.rect(14, currentY - 5, 297 - 28, 8, 'F'); 
        
        // Left Side: Name and Shift
        doc.text(`Name: ${workerName}    |    Shift: ${shift}`, 16, currentY);
        
        // Right Side: Date Range
        doc.text(formattedDateRange, 297 - 16, currentY, { align: "right" });

        const tableBody = [];
        let totalEarnedSum = 0;
        let totalRecoverySum = 0;
        let totalPaidSum = 0;

        data.forEach(p => {
            const isSalary = p.type === 'salary';
            const amount = parseFloat(p.amount) || 0;
            const recovery = parseFloat(p.advance_adjusted) || 0;
            const totalEarned = isSalary ? (amount + recovery) : 0;
            
            if(isSalary) {
                totalEarnedSum += totalEarned;
                totalRecoverySum += recovery;
            }
            totalPaidSum += amount;

            tableBody.push([
                formatDate(p.date), 
                p.type.toUpperCase(), 
                isSalary ? (p.salary_period ? p.salary_period.toUpperCase() : '-') : '-', 
                isSalary ? `Rs. ${totalEarned.toFixed(0)}` : '-', 
                isSalary && recovery > 0 ? `Rs. ${recovery.toFixed(0)}` : '-', 
                `Rs. ${amount.toFixed(0)}`, 
                (p.mode || '').toUpperCase()
            ]);
        });

        // 🌟 GRAND TOTAL ROW 🌟
        tableBody.push([
            { content: 'GRAND TOTAL', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalEarnedSum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalRecoverySum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalPaidSum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: '', styles: { fillColor: [241, 245, 249] } }
        ]);

        autoTable(doc, {
            head: [['Date', 'Type', 'Period', 'Total Earned', 'Less Recov.', 'Net Paid', 'Mode']],
            body: tableBody,
            startY: 60, // Starts exactly below the Name/Date strip
            theme: 'grid',
            headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' },
            alternateRowStyles: { fillColor: [247, 247, 247] },
            styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', cellPadding: 4, textColor: [15, 23, 42] },
            columnStyles: { 0: { fontStyle: 'bold', halign: 'left' }, 1: { halign: 'left', fontStyle: 'bold' } },
            margin: { left: 14, right: 14 }
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text(`Page ${i} of ${totalPages}`, 297 - 14, 210 - 10, { align: "right" });
        }
        
        doc.save(`My_Payment_Report_${reportDateStart || getToday()}.pdf`); 
        if(window.speechSynthesis) speakHinglish(" आपकी पीडीएफ रिपोर्ट फाइल सफलतापूर्वक डाउनलोड हो गई है।");
        setShowReportModal(false);
    };

    // --- DOUBLE-CLICK MEMORY LOGIC (Owner side perfection added) ---
    const clickMemory = useRef({ id: null, time: 0 });

    const speakHinglish = (msg) => {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        
        let processedMsg = msg
            .replace(/Rs\./ig, 'रुपये')
            .replace(/kg/ig, 'किलो')
            .replace(/\btab\b/ig, 'टॅब')
            .replace(/\bm\b/ig, 'मीटर')
            .replace(/\bmar\b/ig, 'March')
            .replace(/\bnht\b/ig, 'NIGHT')
            .replace(/\.0+(?!\d)/g, ''); 
            
        const utterance = new SpeechSynthesisUtterance(processedMsg);
        utterance.lang = 'hi-IN'; 
        utterance.rate = 0.9; 
        
        setTimeout(() => {
            window.speechSynthesis.speak(utterance);
        }, 50);
    };

   // Smart Local Voice Click Wrapper for perfect Double-Click Confirmations
    const handleLocalVoiceClick = (e, voiceText, actionFunction, actionId = null) => {
        // e.preventDefault() input fields ke click (calendar open) ko block kar raha tha
        if (e && actionFunction) { e.preventDefault(); e.stopPropagation(); }

        // Agar voice setting off hai, toh bina double-click ke seedha action chalao
        if (!voiceAssist) {
            if (actionFunction) actionFunction();
            return;
        }

        const uid = actionId || voiceText;
        const now = Date.now();
        const timeDiff = now - clickMemory.current.time;

        if (clickMemory.current.id === uid && timeDiff < 20000) {
            if (actionFunction) actionFunction();
            clickMemory.current = { id: null, time: 0 }; 
        } else {
            speakHinglish(voiceText);
            clickMemory.current = { id: uid, time: now }; 
        }
    };

    // --- FETCH WORKER PAYMENT HISTORY ---
    useEffect(() => {
        if (!user || !user.id) return;

        const fetchHistory = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}/payment_api.php?action=get_history`);
                const json = await res.json();
                if (json.status === 'success') {
                    const myData = json.data.filter(p => String(p.worker_id) === String(user.id));
                    setHistory(myData);
                }
            } catch (error) {
                console.error("Failed to fetch payment history", error);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [user]);

    // --- CALCULATE STATS ---
    const stats = useMemo(() => {
        let data = { sTotal: 0, aTotal: 0, rTotal: 0 };
        history.forEach(p => {
            const amt = parseFloat(p.amount) || 0;
            const rec = parseFloat(p.advance_adjusted) || 0;
            if (p.type === 'salary') { 
                data.sTotal += amt; 
                data.rTotal += rec; 
            } else if (p.type === 'advance') { 
                data.aTotal += amt; 
            }
        });
        return data;
    }, [history]);


    const toggleRow = (id) => {
        setExpandedTxn(expandedTxn === id ? null : id);
    };

    // --- DYNAMIC VOICE MESSAGE GENERATOR FOR WORKER ---
    const getWorkerTransactionVoiceMsg = (p, isExpanded) => {
        if (isExpanded) return " सैलरी की जानकारी बंद की जा रही है।";

        const dateObj = new Date(p.date);
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const dateFormatted = `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
        const modeClean = (p.mode || '').replace('-', ' ');

        let msg = "";
        if (p.type === 'salary') {
            const periodStr = p.salary_period === 'monthly' ? 'महीने' : 'हफ्ते';
            const recovery = parseFloat(p.advance_adjusted) || 0;
            const paid = parseFloat(p.amount) || 0;
            const total = paid + recovery;

            if (recovery > 0) {
                msg = `${dateFormatted} को आपको ${periodStr} की सैलरी ${formatVoiceCurrency(total)} बनी थी, जिसमें से पुराना एडवांस ${formatVoiceCurrency(recovery)} काट लिया गया है, और आपको ${formatVoiceCurrency(paid)} ${modeClean} दिए गए हैं।`;
            } else {
                msg = `${dateFormatted} को आपको ${periodStr} की सैलरी ${formatVoiceCurrency(paid)} ${modeClean} दिए गए हैं।`;
            }
            msg += " इसका पूरा प्रोडक्शन और रेट देखने के लिए, इस पर दोबारा क्लिक करें।";
        } else if (p.type === 'advance') {
            const paid = parseFloat(p.amount) || 0;
            msg = `${dateFormatted} को आपने एडवांस रकम ${formatVoiceCurrency(paid)} ${modeClean} लिए हैं।`;
        } else {
             msg = `यह ${dateFormatted} का Record है जिसकी रकम ${formatVoiceCurrency(p.amount)} है।`;
        }
        return msg;
    };

    return (
        <div style={{...styles.container, background: appBg}}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap'); 
                * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; } 
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
                
                /* 👇 SETTINGS OVERRIDE CSS 👇 */
                .force-blue { color: #3B82F6 !important; }
                .force-red { color: #D32F2F !important; }
                .force-green { color: #10B981 !important; }
                
               /* 🚀 APPLE-LIKE ULTRA SMOOTH EXPERIENCE (120Hz FEEL) 🚀 */
                * { 
                    -webkit-tap-highlight-color: transparent; 
                    scroll-behavior: smooth; 
                    text-rendering: optimizeLegibility; 
                }
                body { 
                    -webkit-font-smoothing: antialiased; 
                    -moz-osx-font-smoothing: grayscale; 
                    overscroll-behavior-y: none; 
                }
                /* Invisible Scrollbar */
                ::-webkit-scrollbar { display: none; width: 0px; background: transparent; } 
                
               /* Smooth 60fps/120fps Button & Input Animations */
                button, input, select {
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    will-change: transform, opacity;
                }
            `}</style>

            <button id="trigger-pdf-download" style={{display: 'none'}} onClick={() => setShowReportModal(true)}></button>
            
            <div style={styles.content}>

                {/* 🌟 NAYA: PENDING SHIFT & RATE ALERTS 🌟 */}
                {(user?.pending_shift_start || user?.pending_rate > 0) && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        {user?.pending_shift_start && (
                            <div style={{ flex: 1, background: '#FEF3C7', border: '1px solid #FDE68A', padding: '10px 14px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 10px rgba(217, 119, 6, 0.1)' }}>
                                <Clock size={18} color="#D97706" />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '600', color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Next Shift Timing</span>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#92400E' }}>{formatTimeForDisplay(user.pending_shift_start)} - {formatTimeForDisplay(user.pending_shift_end)}</span>
                                </div>
                            </div>
                        )}
                        {user?.pending_rate > 0 && (
                            <div style={{ flex: 1, background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '10px 14px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.1)' }}>
                                <div style={{ background: '#16A34A', borderRadius: '50%', padding: '4px' }}>
                                    <IndianRupee size={14} color="#ffffff" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '600', color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Rate Applied</span>
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#166534' }}>₹{user.pending_rate} <span style={{fontSize:'8px', fontWeight:'500'}}>/ Meter</span></span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* --- PREMIUM STAT BOXES (WITH COMBINED ADVANCE CARD) --- */}
                <div style={{marginBottom:'16px'}}>
                    
                    {/* Row 1: Salary Received Premium Card */}
                    <div 
                        onClick={(e) => {
                            const isAlreadyActive = statFilterType === 'salary';
                            const voiceMsg = isAlreadyActive 
                                ? "सारे रिकॉर्ड्स दिखाए जा रहे हैं।" 
                                : `आपको अब तक टोटल सैलरी ${formatVoiceCurrency(stats.sTotal)} मिली है। सिर्फ सैलरी का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।`;
                            handleLocalVoiceClick(e, voiceMsg, () => setStatFilterType(isAlreadyActive ? 'all' : 'salary'), 'filter-salary');
                        }} 
                        style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '20px',
                            padding: '16px',
                            boxShadow: '0 8px 24px rgba(5, 150, 105, 0.15), inset 0 1px 2px rgba(255,255,255,0.8)',
                            border: statFilterType === 'salary' ? '2px solid #10B981' : '1px solid rgba(255,255,255,0.5)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            width: '100%',
                            overflow: 'hidden',
                            marginBottom: '12px'
                        }}
                    >
                        <div style={{ position: 'absolute', top: '-20px', left: '-20px', width: '100px', height: '100px', background: 'radial-gradient(circle, #34D399 0%, transparent 70%)', opacity: 0.15, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                        <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', zIndex: 1 }}>
                            <h3 style={{fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 6px 0'}}>
                                Total Salary Received
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontSize: '18px', color: '#059669', fontWeight: '900' }}>₹</span>
                                <span style={{ fontSize: '32px', fontWeight: '900', color: '#0F172A', letterSpacing: '-1px' }}>
                                    {formatCurrency(stats.sTotal).replace('Rs. ', '')}
                                </span>
                            </div>
                        </div>
                        <div style={{ 
                            background: 'linear-gradient(135deg, #059669, #10B981)', 
                            padding: '12px', 
                            borderRadius: '16px', 
                            boxShadow: '0 6px 16px rgba(5, 150, 105, 0.3)', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', zIndex: 1
                        }}>
                            <Banknote size={26} strokeWidth={2.5} />
                        </div>
                    </div>

                    {/* Row 2: Combined Advance & Recovered Premium Cards */}
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                        
                        {/* 🌟 COMBINED ADVANCE CARD (Taken + Balance Due) 🌟 */}
                        <div 
                            onClick={(e) => {
                                const isAlreadyActive = statFilterType === 'advance';
                                const dueAmt = parseFloat(user?.advance_balance || 0);
                                const voiceMsg = isAlreadyActive 
                                    ? "सारे रिकॉर्ड्स दिखाए जा रहे हैं।" 
                                    : ` आपने अब तक टोटल एडवांस ${formatVoiceCurrency(stats.aTotal)} लिया है${dueAmt > 0 ? `, और आपका बाकी एडवांस बैलेंस ${formatVoiceCurrency(dueAmt)} है` : ''}। सिर्फ एडवांस का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।`;
                                handleLocalVoiceClick(e, voiceMsg, () => setStatFilterType(isAlreadyActive ? 'all' : 'advance'), 'filter-advance');
                            }} 
                            style={{
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '20px',
                                padding: '16px',
                                boxShadow: '0 8px 24px rgba(220, 38, 38, 0.1), inset 0 1px 2px rgba(255,255,255,0.8)',
                                border: statFilterType === 'advance' ? '2px solid #DC2626' : '1px solid rgba(255,255,255,0.5)',
                                display: 'flex', flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                width: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'radial-gradient(circle, #F87171 0%, transparent 70%)', opacity: 0.15, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 1, marginBottom: '12px' }}>
                                <div style={{ 
                                    background: 'linear-gradient(135deg, #DC2626, #EF4444)', 
                                    padding: '8px', 
                                    borderRadius: '12px', 
                                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff'
                                }}>
                                    <Wallet size={16} />
                                </div>
                                <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Advances Taken
                                </span>
                            </div>

                            <div style={{ zIndex: 1, display: 'flex', alignItems: 'baseline', gap: '4px', borderBottom: parseFloat(user?.advance_balance || 0) > 0 ? '1px dashed #E2E8F0' : 'none', paddingBottom: parseFloat(user?.advance_balance || 0) > 0 ? '12px' : '0', marginBottom: parseFloat(user?.advance_balance || 0) > 0 ? '12px' : '0' }}>
                                <span style={{ fontSize: '16px', color: '#DC2626', fontWeight: '900' }}>₹</span>
                                <span style={{ fontSize: '24px', fontWeight: '900', color: '#0F172A', letterSpacing: '-0.5px' }}>
                                    {formatCurrency(stats.aTotal).replace('Rs. ', '')}
                                </span>
                            </div>

                            {/* SMART DUE BALANCE SUB-SECTION */}
                            {parseFloat(user?.advance_balance || 0) > 0 && (
                                <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: '6px', background: '#FEF2F2', padding: '10px', borderRadius: '12px', border: '1px solid #FECACA' }}>
                                    <span style={{ fontSize: '9px', color: '#991B1B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <div style={{width:'4px', height:'4px', borderRadius:'50%', background:'#DC2626'}}></div> Balance Due
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                        <span style={{ fontSize: '14px', color: '#DC2626', fontWeight: '900' }}>₹</span>
                                        <span style={{ fontSize: '18px', fontWeight: '900', color: '#DC2626', letterSpacing: '-0.5px' }}>
                                            {parseFloat(user?.advance_balance || 0).toFixed(0)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recovered Card */}
                        <div 
                            onClick={(e) => {
                                const isAlreadyActive = statFilterType === 'recovered';
                                const voiceMsg = isAlreadyActive 
                                    ? "सारे रिकॉर्ड्स दिखाए जा रहे हैं।" 
                                    : `आपकी सैलरी से अब तक टोटल ${formatVoiceCurrency(stats.rTotal)} एडवांस वापस कटा है। एडवांस कटने का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।`;
                                handleLocalVoiceClick(e, voiceMsg, () => setStatFilterType(isAlreadyActive ? 'all' : 'recovered'), 'filter-recovered');
                            }} 
                            style={{
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '20px',
                                padding: '16px',
                                boxShadow: '0 8px 24px rgba(2, 132, 199, 0.1), inset 0 1px 2px rgba(255,255,255,0.8)',
                                border: statFilterType === 'recovered' ? '2px solid #0284C7' : '1px solid rgba(255,255,255,0.5)',
                                display: 'flex', flexDirection: 'column', gap: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                width: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'radial-gradient(circle, #38BDF8 0%, transparent 70%)', opacity: 0.15, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 1, marginBottom: 'auto' }}>
                                <div style={{ 
                                    background: 'linear-gradient(135deg, #0284C7, #38BDF8)', 
                                    padding: '8px', 
                                    borderRadius: '12px', 
                                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff'
                                }}>
                                    <RefreshCw size={16} />
                                </div>
                                <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Paid Back
                                </span>
                            </div>

                            <div style={{ zIndex: 1, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontSize: '16px', color: '#0284C7', fontWeight: '900' }}>₹</span>
                                <span style={{ fontSize: '24px', fontWeight: '900', color: '#0F172A', letterSpacing: '-0.5px' }}>
                                    {formatCurrency(stats.rTotal).replace('Rs. ', '')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 🌟 PREMIUM FILTER & REPORT BOX WITH SEARCH STRIP 🌟 */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)', 
                        backdropFilter: 'blur(24px)', 
                        borderRadius: '20px', 
                        padding: '12px', 
                        boxShadow: '0 10px 25px rgba(0,0,0,0.04), inset 0 2px 0 rgba(255,255,255,1)', 
                        border: '1px solid rgba(255,255,255,0.7)' 
                    }}>
                        {!isSearchExpanded && !startDate && !endDate ? (
                            <div onClick={(e) => { handleLocalVoiceClick(e, " डेट के हिसाब से पेमेंट हिस्ट्री सर्च करने के लिए इस सर्च बॉक्स पर क्लिक करें।", () => setIsSearchExpanded(true), 'btn-search'); }} 
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.04)', transition: 'all 0.2s', marginBottom: '0px' }}> {/* 👈 मार्जिन 0px कर दिया */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Search size={14} color="#64748B"/>
                                    <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '500' }}>Search by Date...</span>
                                </div>
                                <ChevronDown size={14} color="#94A3B8"/>
                            </div>
                        ) : (
                            <div style={{ background: '#fff', borderRadius: '14px', padding: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)', animation: 'slideDown 0.2s ease-out', marginBottom: '0px' }}> {/* 👈 मार्जिन 0px कर दिया */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '10px', fontWeight: '600', color: '#475569', letterSpacing: '0.5px' }}>DATE RANGE</span>
                                    <button onClick={(e) => { handleLocalVoiceClick(e, " सर्च फिल्टर हटाने के लिए इस पर दोबारा क्लिक करें।", () => {setStartDate(''); setEndDate(''); setIsSearchExpanded(false);}, 'btn-clear'); }} 
                                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', color: '#DC2626', transition: 'background 0.2s' }}>
                                        <X size={12} /> <span style={{ fontSize: '8px', fontWeight: '600', marginLeft: '2px' }}>CLEAR</span>
                                    </button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                    <input type="date" value={startDate} onClick={(e) => handleLocalVoiceClick(e, " किस दिन का पेमेंट रिकॉर्ड देखना चाहते है, उस दिन की तारीख चुनें।", null)} onChange={(e) => setStartDate(e.target.value)} 
                                        style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px', outline: 'none', background: '#F8FAFC', color: '#0F172A', fontWeight: '500' }} />
                                    <span style={{ color: '#94A3B8', fontWeight: '600' }}>-</span>
                                    <input type="date" value={endDate} onClick={(e) => handleLocalVoiceClick(e, " किस दिन तक का पेमेंट रिकॉर्ड देखना चाहते है, उस दिन की तारीख चुनें।", null)} onChange={(e) => setEndDate(e.target.value)} 
                                        style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px', outline: 'none', background: '#F8FAFC', color: '#0F172A', fontWeight: '500' }} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

              {/* --- PREMIUM TRANSACTION HISTORY (GLASSMORPHISM & FLOATING CARDS - EDGES CURVED) --- */}
                <div className="premium-glass animate-in" style={{ animationDelay: '0.2s', padding: '0', overflow: 'hidden', borderRadius: '24px', border: '1px solid #E2E8F0' }}>
                    
                    {/* Modern Header */}
                    <div style={{ padding: '20px', background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>                        <div style={{display:'flex', flexDirection: 'column'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <div style={{ background: '#1E293B', padding: '6px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)' }}>
                                    <Clock size={16} color="#FFFFFF"/>
                                </div>
                                <span style={{fontSize:'14px', fontWeight:'800', color:'#0F172A'}}>My Transactions</span>
                            </div>
                        </div>

                        {statFilterType !== 'all' && (
                            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '6px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '9px', color: '#0F172A', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {statFilterType}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {/* List Area */}
                    <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', minHeight:'200px' }}>
                       {loading ? (
                            <div style={{textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'}}>
                                <p style={{color:'#64748B', fontSize:'12px', fontWeight:'600'}}>Fetching records...</p>
                            </div>
                        ) : filteredHistory.length === 0 ? (
                            <div style={{textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'}}>
                                <div style={{ background: '#FFFFFF', padding: '16px', borderRadius: '50%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                    <AlertCircle size={28} color="#94A3B8" />
                                </div>
                                <p style={{color:'#64748B', fontSize:'12px', fontWeight:'600', margin:0}}>No records found.</p>
                            </div>
                        ) : (
                            filteredHistory.map((p, i) => {
                                const isRecoveredFilter = statFilterType === 'recovered';
                                const mainOpacity = isRecoveredFilter ? 0.3 : 1;
                                
                                const gross = parseFloat(p.amount) + parseFloat(p.advance_adjusted);
                                const rec = parseFloat(p.advance_adjusted);
                                const paid = parseFloat(p.amount);
                                const isSalary = p.type === 'salary';

                                return (
                                    <div key={p.id} 
                                        onClick={(e) => {
                                            const voiceMsg = getWorkerTransactionVoiceMsg(p, false);
                                            handleLocalVoiceClick(e, voiceMsg, () => {
                                                setViewData(p);
                                                setShowViewModal(true);
                                            }, `hist-${p.id}`);
                                        }}
                                        style={{
                                            background: '#ffffff', borderRadius: '16px', padding: '16px 16px 16px 20px', 
                                            boxShadow: '0 6px 16px rgba(15,23,42,0.03)', border: '1px solid #E2E8F0', 
                                            display: 'flex', flexDirection: 'column', cursor:'pointer',
                                            transition: 'all 0.2s ease', marginBottom: '12px', position: 'relative', overflow: 'hidden'
                                        }}
                                    >
                                        {/* 🌟 Left Accent Color Strip 🌟 */}
                                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', background: isSalary ? '#10B981' : '#EF4444' }} />
                                        
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                                            <div style={{flex: 1}}>
                                                <div style={{opacity: mainOpacity, transition:'opacity 0.2s'}}>
                                                    
                                                    {/* Title & Badge */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}>
                                                        <span style={{fontSize:'13px', fontWeight:'800', color:'#0F172A', textTransform:'capitalize'}}>
                                                            {isSalary ? (p.salary_period ? `${p.salary_period} Salary` : 'Salary') : 'Advance'}
                                                        </span>
                                                        {p.is_edited == 1 && (
                                                            <span style={{fontSize:'8px', background:'#FEF2F2', color:'#DC2626', padding:'2px 6px', borderRadius:'6px', fontWeight:'800', letterSpacing:'0.5px'}}>
                                                                EDITED
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Date & Payment Mode */}
                                                    <div style={{ fontSize: '10px', color: '#64748B', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <CalIcon size={10} color="#94A3B8" /> {formatDateTime(p.date)}
                                                        </span>
                                                        <span style={{ color: '#E2E8F0' }}>•</span>
                                                        {(() => {
                                                            const mStr = (p.mode || '').toLowerCase();
                                                            let mName = p.mode; let mColor = '#0F172A'; let mIcon = null; 
                                                            
                                                            if (mStr === 'cash') {
                                                                mName = 'Cash'; mColor = '#10B981';
                                                                mIcon = <Banknote size={10} color={mColor} />;
                                                            } else if (mStr.includes('phonepe')) {
                                                                mName = 'PhonePe'; mColor = '#0F172A';
                                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="PhonePe" style={{width:'10px', height:'10px', borderRadius:'2px'}}/>;
                                                            } else if (mStr.includes('gpay') || mStr.includes('google')) {
                                                                mName = 'GPay'; mColor = '#0F172A';
                                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="GPay" style={{width:'10px', height:'10px', borderRadius:'2px'}}/>;
                                                            } else if (mStr.includes('paytm')) {
                                                                mName = 'Paytm'; mColor = '#0F172A';
                                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="Paytm" style={{width:'10px', height:'10px', borderRadius:'2px'}}/>;
                                                            } else if (mStr.includes('bharatpe')) {
                                                                mName = 'BharatPe'; mColor = '#0F172A';
                                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="BharatPe" style={{width:'10px', height:'10px', borderRadius:'2px'}}/>;
                                                            } else if (mStr.includes('online')) {
                                                                mName = 'Online'; mColor = '#3B82F6';
                                                                mIcon = <CreditCard size={10} color={mColor} />;
                                                            }

                                                            return (
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                    {mIcon}
                                                                    <span style={{ color: mColor, fontWeight: '700', fontSize: '9px', textTransform: 'capitalize' }}>{mName}</span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                                
                                              {/* 🌟 Executive Calculations Breakdown Bubble (Synchronized with Receipt Ledger) 🌟 */}
                                {isSalary && rec > 0 && (
                                    <div style={{
                                        fontSize: '10px', color: '#0F172A', marginTop: '12px', 
                                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px',
                                        background: '#F8FAFC', padding: '6px 10px', borderRadius: '8px',
                                        border: '1px solid #E2E8F0', opacity: 1, width: 'fit-content'
                                    }}>
                                        <span className="force-blue" style={{ fontWeight: '700' }}>Gross: ₹{Number(gross.toFixed(0))}</span>
                                        <span style={{ color: '#94A3B8', fontWeight: '700' }}>|</span>
                                        <span className="force-red" style={{ fontWeight: '700' }}>Deduction: ₹{Number(rec.toFixed(0))}</span>
                                        <span style={{ color: '#94A3B8', fontWeight: '700' }}>=</span>
                                        <span className="force-green" style={{ fontWeight: '700' }}>Net Paid: ₹{Number(paid.toFixed(0))}</span>
                                    </div>
                                )}
                                            </div>
                                            
                                            {/* Right Side Amount (Fixed Black Overriding Bug) */}
                                            <div style={{textAlign:'right', minWidth: '80px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', height: '100%'}}>
                                                <span className={isSalary ? 'force-green' : 'force-red'} style={{fontSize:'18px', fontWeight:'900', letterSpacing: '-0.5px'}}>
                                                    ₹{p.amount}
                                                </span>
                                                <span style={{fontSize:'9px', fontWeight:'700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px'}}>
                                                    {isSalary ? 'Net Paid' : 'Received'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

               <div style={{textAlign:'center', marginTop:'20px'}}>
                    <p style={{fontSize:'8px', fontWeight:'500', color:'#444', padding:'0 16px', lineHeight:'1.5'}}>
                        Yeh records direct Owner ke system se sync hote hain.<br/>Kisi bhi discrepancy (galti) ke liye apne Owner se contact karein.
                    </p>
                </div>

            </div>

            {/* ===== 🌟 EXECUTIVE CLASSIC UI VIEW MODAL (OPTIMIZED SCROLL FITTING FOR WORKER) 🌟 ===== */}
            <Modal
                isOpen={showViewModal && !!viewData}
                onClose={() => setShowViewModal(false)}
                title="Transaction Receipt"
                icon={viewData?.type === 'salary' ? CreditCard : Wallet}
                iconColor="#0F172A"
                iconBg="#F1F5F9"
                maxWidth="420px"
            >
                {viewData && (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', background: 'transparent', textAlign: 'center' }}>
                        
                        {/* 💳 Minimalist Classic Header Statement Panel - Compact Padding */}
                        <div style={{ padding: '0px 0 2px 0' }}>
                            {/* Solid Classic Status Circle */}
                            <div style={{ 
                                width: '40px', height: '40px', borderRadius: '50%', 
                                background: viewData.type === 'salary' ? '#E6F4EA' : '#FCE8E6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                margin: '0 auto 8px auto', border: viewData.type === 'salary' ? '1px solid #A7F3D0' : '1px solid #FCA5A5'
                            }}>
                                {viewData.type === 'salary' ? (
                                    <CreditCard size={16} color="#137333" />
                                ) : (
                                    <Wallet size={16} color="#C5221F" />
                                )}
                            </div>

                            <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.2px', display: 'block', marginBottom: '2px' }}>
                                {viewData.type === 'salary' ? 'Salary Disbursed Statement' : 'Advance Issued Statement'}
                            </span>
                            
                            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0F172A', margin: 0, letterSpacing: '-1px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                ₹{(Number(viewData.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h2>
                        </div>

                       {/* 📊 CLASSIC ACCOUNTING LEDGER MATRIX (Streamlined Vertical Balance Sheet) */}
                        {viewData.type === 'salary' && parseFloat(viewData.advance_adjusted) > 0 && (
                            <div style={{ 
                                background: '#F8FAFC', 
                                borderRadius: '14px', 
                                padding: '12px 14px', 
                                border: '1px solid #E2E8F0',
                                width: '100%',
                                textAlign: 'left'
                            }}>
                                <div style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px' }}>
                                    Breakdown Ledger
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '11px', color: '#475569', fontWeight: '500' }}>Gross Earned Salary</span>
                                        <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '700' }}>
                                            ₹{Number((parseFloat(viewData.amount) + parseFloat(viewData.advance_adjusted)).toFixed(2)).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="force-red" style={{ fontSize: '11px', fontWeight: '600' }}>Less: Advance Deduction</span>
                                        <span className="force-red" style={{ fontSize: '11px', fontWeight: '700' }}>
                                            - ₹{Number(parseFloat(viewData.advance_adjusted).toFixed(2)).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div style={{ height: '1px', background: '#E2E8F0', margin: '2px 0' }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '800' }}>Net Payable Received</span>
                                        <span className="force-green" style={{ fontSize: '13px', fontWeight: '900' }}>
                                            ₹{Number(parseFloat(viewData.amount).toFixed(2)).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* 📋 SYMMETRICAL REVENUE MANIFEST MATRIX - Tight Paddings & Worker Params */}
                        <div style={{ 
                            background: '#FFFFFF', 
                            borderRadius: '14px', 
                            padding: '2px 14px', 
                            border: '1px solid #E2E8F0', 
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%'
                        }}>
                            {/* Core Item: Timeline Timestamp */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
                                <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <CalIcon size={12} color="#94A3B8" /> Date & Time
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                                    {formatDateTime(viewData.date)}
                                </span>
                            </div>
                            
                            {/* Core Item: Financial Route Mode */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: viewData.type === 'salary' ? '1px solid #F1F5F9' : 'none' }}>
                                <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <Banknote size={12} color="#94A3B8" /> Payment Mode
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A' }}>
                                    {(() => {
                                        const mStr = (viewData.mode || '').toLowerCase();
                                        let mName = viewData.mode;
                                        let mIcon = null;
                                        
                                        if (mStr === 'cash') {
                                            mName = 'Cash';
                                            mIcon = <Banknote size={12} color="#10B981" style={{ marginRight: '6px' }} />;
                                        } else if (mStr.includes('phonepe')) {
                                            mName = 'PhonePe';
                                            mIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                        } else if (mStr.includes('gpay')) {
                                            mName = 'GPay';
                                            mIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                        } else if (mStr.includes('paytm')) {
                                            mName = 'Paytm';
                                            mIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                        } else if (mStr.includes('bharatpe')) {
                                            mName = 'BharatPe';
                                            mIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                        } else if (mStr.includes('online')) {
                                            mName = 'Online Transfer';
                                            mIcon = <CreditCard size={12} color="#3B82F6" style={{ marginRight: '6px' }} />;
                                        }

                                        return (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', textTransform: 'capitalize' }}>
                                                {mIcon} <span>{mName}</span>
                                            </div>
                                        );
                                    })()}
                                </span>
                            </div>

                            {/* Worker Added Detail: Total Production Production */}
                            {viewData.type === 'salary' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
                                    <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Ruler size={12} color="#94A3B8" /> Total Production
                                    </span>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A' }}>
                                        {viewData.total_production || '0'} Meter
                                    </span>
                                </div>
                            )}

                            {/* Worker Added Detail: Work Production Rate */}
                            {viewData.type === 'salary' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                                    <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Calculator size={12} color="#94A3B8" /> Rate
                                    </span>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A' }}>
                                        ₹{viewData.rate || '0'}/Meter
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* 🗓️ PRODUCTION INTERVAL METADATA BLOCK - Slim Bounds */}
                        {((viewData.prod_start_date && viewData.prod_start_date !== '0000-00-00') || viewData.type === 'salary') && (
                            <div style={{ 
                                background: '#F8FAFC', 
                                borderRadius: '12px', 
                                padding: '10px 14px', 
                                border: '1px solid #E2E8F0', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                width: '100%',
                                textAlign: 'left'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                    <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Accounting Cycle Duration
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '700' }}>
                                        {(viewData.prod_start_date && viewData.prod_start_date !== '0000-00-00') ? `${formatDate(viewData.prod_start_date)} – ${formatDate(viewData.prod_end_date)}` : 'Cycle Bounds Empty'}
                                    </span>
                                </div>
                                {viewData.type === 'salary' && (
                                    <span style={{ 
                                        background: '#FFFFFF', color: '#475569', 
                                        padding: '3px 8px', borderRadius: '6px', 
                                        fontSize: '9px', fontWeight: '800', 
                                        textTransform: 'uppercase', letterSpacing: '0.5px', 
                                        border: '1px solid #D1D5DB'
                                    }}>
                                        {viewData.salary_period || 'Weekly'}
                                    </span>
                                )}
                            </div>
                        )}

                        {viewData.remarks && (
                            <div style={{ 
                                padding: '10px 12px', 
                                borderRadius: '10px', 
                                borderLeft: '3px solid #64748B',
                                background: '#F8FAFC',
                                textAlign: 'left',
                                width: '100%'
                            }}>
                                <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '1px' }}>Statement Note</span>
                                <p style={{ margin: 0, fontSize: '11px', color: '#475569', fontWeight: '500', fontStyle: 'italic' }}>"{viewData.remarks}"</p>
                            </div>
                        )}
                        
                    </div>
                )}
            </Modal>

           {/* ===== 🌟 NEW UI MODAL FOR PDF EXPORT 🌟 ===== */}
            <Modal 
                isOpen={showReportModal} 
                onClose={() => setShowReportModal(false)} 
                title="Download Payments Report" 
                icon={Download} 
                iconColor="#2563EB" 
                iconBg="#DBEAFE"
                maxWidth="340px"
            >
                <div style={{padding: '10px 0'}}>
                    <label style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px', display: 'block', letterSpacing: '0.5px' }}>Select Start Date</label>
                    <input type="date" style={{width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '11px', outline: 'none', background: '#ffffff', color:'#0F172A', fontWeight:'600', boxSizing: 'border-box', marginBottom:'16px'}} value={reportDateStart} onClick={(e) => handleLocalVoiceClick(e, " किस तारीख का पेमेंट History रिपोर्ट पीडीएफ Download करनी है wo तारीख चुनें।", null)} onChange={(e)=>setReportDateStart(e.target.value)} />
                    
                    <label style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px', display: 'block', letterSpacing: '0.5px' }}>Select End Date</label>
                    <input type="date" style={{width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '11px', outline: 'none', background: '#ffffff', color:'#0F172A', fontWeight:'600', boxSizing: 'border-box', marginBottom:'24px'}} value={reportDateEnd} onClick={(e) => handleLocalVoiceClick(e, " किस तारीख तक का पेमेंट History रिपोर्ट पीडीएफ Download करनी है wo तारीख चुनें।", null)} onChange={(e)=>setReportDateEnd(e.target.value)} />
                    
                    <button 
                        onClick={(e) => handleLocalVoiceClick(e, " अपना पेमेंट रिपोर्ट पीडीएफ में डाउनलोड करने के लिए इस पर दोबारा क्लिक करें।", handleDownloadPdf, 'confirm-download-pdf')} 
                        style={{width: '100%', background: '#2563EB', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s'}}
                    >
                        <Download size={16} color="#ffffff" /> Download Payments Report
                    </button>
                </div>
            </Modal>

        </div>
    );
};

const styles = {
    container: { 
        minHeight: '100vh', 
        
        width: '100%', 
        height: '100%', 
        transition: 'background 0.3s ease', 
        overflowY: 'auto', 
        /* 🚀 iOS LEVEL GPU ACCELERATION 🚀 */
        WebkitOverflowScrolling: 'touch', 
        transform: 'translateZ(0)', 
        backfaceVisibility: 'hidden', 
        perspective: 1000,
        willChange: 'transform, scroll-position'
    },
    content: { padding: '16px',paddingBottom: '20px', maxWidth: '600px', margin: '0 auto' }
};